import type { EnqueueAction, OutboundBeaconCommand } from '../types/analytics';
import type { AnalyticsClient, FlowHandle, StartFlowOptions } from '../types/analytics-client';
import type { AnalyticsTransport } from '../types/analytics-transport';
import type { ClockPort } from '../types/clock';
import { systemClockPort } from '../types/clock';
import type {
  BaselineDigitalData,
  PageContextInput,
  PropertyContextInput,
  UserContextInput,
} from '../types/digital-data';
import type { CoordinatorDebugSnapshot, CoordinatorEventLogEntry } from './audit-types';
import { OutboundCommandQueue } from './command-queue';
import {
  DEFERRED_QUEUE_MAX,
  EVENT_LOG_ENTRIES_CAPACITY,
  FLUSH_DEFAULT_TIMEOUT_MS,
  QUEUE_MAX_AGE_MS,
  QUEUE_MAX_DEPTH,
} from './constants';
import { DigitalDataComposer } from './digital-data-composer';
import { ForbiddenPathPatchError } from './digital-data-patch';
import { FlowManager } from './flow-manager';
import { ReadinessController } from './readiness';

type AnalyticsCoordinatorDependencies = {
  transport: AnalyticsTransport;
  readiness: ReadinessController;
  clock?: ClockPort;
  onEventLogEntry?: (entry: CoordinatorEventLogEntry) => void;
  queueBounds?: {
    maxDepth: number;
    maxAgeMs: number;
    deferredMaxDepth: number;
  };
};

const FLUSH_IDLE_POLL_FRAME_MS = 16;

export class AnalyticsCoordinator implements AnalyticsClient {
  private readonly clock: ClockPort;
  private readonly flowManager: FlowManager;
  private readonly eventLogEntries: CoordinatorEventLogEntry[] = [];
  private readonly digitalDataComposer = new DigitalDataComposer();
  private readonly queue: OutboundCommandQueue;
  private unsubscribeReady: (() => void) | null = null;
  private unsubscribeTimeout: (() => void) | null = null;
  private isDispatching = false;
  private eventCounter = 0;
  private activeDispatchRun: Promise<void> | null = null;
  private isDisposed = false;

  constructor(private readonly dependencies: AnalyticsCoordinatorDependencies) {
    this.clock = dependencies.clock ?? systemClockPort;
    const maxDepth = dependencies.queueBounds?.maxDepth ?? QUEUE_MAX_DEPTH;
    const maxAgeMs = dependencies.queueBounds?.maxAgeMs ?? QUEUE_MAX_AGE_MS;
    const deferredMaxDepth = dependencies.queueBounds?.deferredMaxDepth ?? DEFERRED_QUEUE_MAX;

    this.queue = new OutboundCommandQueue({
      clock: this.clock,
      onEventLogEntry: (entry) => {
        this.pushEventLogEntry(entry);
      },
      maxDepth,
      maxAgeMs,
      deferredMaxDepth,
    });
    this.flowManager = new FlowManager({
      clock: this.clock,
      onEventLogEntry: (entry) => {
        this.pushEventLogEntry(entry);
      },
      onFlowEnded: () => {
        void this.triggerDispatch();
      },
      onGateReleased: () => {
        void this.triggerDispatch();
      },
    });

    this.bindReadiness();

    this.pushEventLogEntry({
      type: 'coordinator-init',
      meta: {
        queueMaxDepth: maxDepth,
        queueMaxAgeMs: maxAgeMs,
        deferredQueueMax: deferredMaxDepth,
        flushDefaultTimeoutMs: FLUSH_DEFAULT_TIMEOUT_MS,
      },
    });
  }

  dispose(): void {
    if (this.isDisposed) {
      return;
    }
    this.isDisposed = true;
    this.unbindReadiness();
  }

  /** Re-subscribes readiness handlers after a React Strict Mode simulated unmount. */
  reconnect(): void {
    if (!this.isDisposed) {
      return;
    }
    this.bindReadiness();
  }

  private bindReadiness(): void {
    this.unbindReadiness();
    this.isDisposed = false;
    this.unsubscribeTimeout = this.dependencies.readiness.onTimeout(() => {
      if (this.isDisposed) {
        return;
      }
      this.queue.dropPendingWhileNotReady('readiness-timeout');
    });
    this.unsubscribeReady = this.dependencies.readiness.onReady(() => {
      if (this.isDisposed) {
        return;
      }
      void this.triggerDispatch();
    });
  }

  private unbindReadiness(): void {
    this.unsubscribeReady?.();
    this.unsubscribeTimeout?.();
    this.unsubscribeReady = null;
    this.unsubscribeTimeout = null;
  }

  getDebugSnapshot(): CoordinatorDebugSnapshot {
    const counts = this.queue.getQueueLengths();
    return {
      pendingCount: counts.pendingQueue,
      deferredCount: counts.deferredQueue,
      isDispatching: this.isDispatching,
      eventLogEntriesLength: this.eventLogEntries.length,
    };
  }

  getEventLogEntries(): readonly CoordinatorEventLogEntry[] {
    return [...this.eventLogEntries];
  }

  clearEventLogEntries(): void {
    this.eventLogEntries.length = 0;
  }

  enqueue(action: EnqueueAction): void {
    if (this.isDisposed) {
      return;
    }
    let command: OutboundBeaconCommand;
    try {
      command = this.composeOutboundBeaconCommand(action);
    } catch (error) {
      if (error instanceof ForbiddenPathPatchError) {
        this.pushEventLogEntry({
          type: 'forbidden-path-rejected',
          meta: {
            dcr: action.dcr,
            source: action.source,
            eventType: action.eventType,
            path: error.message,
          },
        });
        return;
      }
      throw error;
    }
    this.queue.enqueue(command, this.isDispatching);

    if (this.isDispatching) {
      return;
    }
    if (!this.dependencies.readiness.isReady() || !this.isTransportCallable()) {
      this.dependencies.readiness.armNotReadyTimer();
      return;
    }
    if (this.flowManager.shouldHoldInPending(action)) {
      return;
    }

    void this.triggerDispatch();
  }

  setBaselineDigitalData(data: BaselineDigitalData): void {
    this.digitalDataComposer.setBaselineDigitalData(data);
  }

  setPageContext(ctx: PageContextInput): void {
    this.digitalDataComposer.setPageContext(ctx);
  }

  setUserContext(ctx: UserContextInput): void {
    this.digitalDataComposer.setUserContext(ctx);
  }

  setPropertyContext(ctx: PropertyContextInput | null): void {
    this.digitalDataComposer.setPropertyContext(ctx);
  }

  startFlow(opts: StartFlowOptions): FlowHandle {
    return this.flowManager.startFlow(opts);
  }

  async flushUntilIdle(opts?: { timeoutMs?: number }): Promise<void> {
    const timeoutMs = opts?.timeoutMs ?? FLUSH_DEFAULT_TIMEOUT_MS;
    const deadline = this.clock.now() + timeoutMs;

    while (this.clock.now() < deadline) {
      await this.triggerDispatch();
      if (this.isIdle()) {
        return;
      }
      await this.waitBeforeNextFlushAttempt(deadline);
    }
  }

  private composeOutboundBeaconCommand(action: EnqueueAction): OutboundBeaconCommand {
    const digitalData = this.digitalDataComposer.composeDigitalData(action);
    const strippedPaths = this.digitalDataComposer.getLastStrippedForbiddenPaths();
    if (strippedPaths.length > 0) {
      this.pushEventLogEntry({
        type: 'forbidden-path-stripped',
        meta: {
          dcr: action.dcr,
          source: action.source,
          eventType: action.eventType,
          paths: strippedPaths,
        },
      });
    }

    return {
      type: 'launch',
      id: this.nextEventId(),
      dcr: action.dcr,
      source: action.source,
      eventTypeHint: action.eventType,
      flowId: action.flowId,
      digitalData,
      enqueuedAt: this.clock.now(),
    };
  }

  private nextEventId(): string {
    this.eventCounter += 1;
    return `evt-${this.eventCounter}`;
  }

  private isTransportCallable(): boolean {
    return this.dependencies.transport.isCallable?.() ?? true;
  }

  private isIdle(): boolean {
    return !this.isDispatching && this.queue.isEmpty() && this.activeDispatchRun === null;
  }

  private triggerDispatch(): Promise<void> {
    if (this.isDisposed) {
      return Promise.resolve();
    }
    if (this.activeDispatchRun) {
      return this.activeDispatchRun;
    }
    this.activeDispatchRun = this.dispatchPendingCommands().finally(() => {
      this.activeDispatchRun = null;
    });
    return this.activeDispatchRun;
  }

  private async dispatchPendingCommands(): Promise<void> {
    if (
      this.isDispatching ||
      !this.dependencies.readiness.isReady() ||
      !this.isTransportCallable()
    ) {
      return;
    }

    this.dependencies.readiness.clearNotReadyTimer();
    await this.dispatchNextCommand();
  }

  private async dispatchNextCommand(): Promise<void> {
    while (
      this.queue.getQueueLengths().pendingQueue > 0 &&
      this.dependencies.readiness.isReady() &&
      this.isTransportCallable()
    ) {
      const command = this.queue.takeNextDispatchableCommand((candidate) =>
        this.flowManager.canDispatchNow(candidate)
      );
      if (!command) {
        return;
      }

      this.isDispatching = true;
      try {
        await this.dependencies.transport.send(command);
      } catch {
        this.pushEventLogEntry({
          type: 'dispatch-error',
          meta: { id: command.id, dcr: command.dcr },
        });
      } finally {
        this.isDispatching = false;
      }
      this.queue.mergeDeferredToBack();
    }
  }

  private pushEventLogEntry(entry: CoordinatorEventLogEntry): void {
    this.eventLogEntries.push(entry);
    if (this.eventLogEntries.length > EVENT_LOG_ENTRIES_CAPACITY) {
      this.eventLogEntries.shift();
    }
    this.dependencies.onEventLogEntry?.(entry);
  }

  private waitBeforeNextFlushAttempt(deadline: number): Promise<void> {
    const remaining = deadline - this.clock.now();
    const delayMs = Math.min(FLUSH_IDLE_POLL_FRAME_MS, Math.max(0, remaining));
    return new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }
}
